import type { RecentStudyType } from '@mathflat/domain/StudentHome'
import { observer } from 'mobx-react'
import { type FC, useCallback, useEffect } from 'react'

import { useStudentAppMediaQuery } from '~/@common/hooks/useMediaQuery'
import { useRepository } from '~/@common/hooks/useRepository'
import { errorHandlerService } from '~/@common/services'
import { commonRepo } from '~/@common/services/repo.service'

import { useMobilePagedStudyList } from '../../@hooks/useMobilePagedStudyList'
import ParentStudyHistoryService from '../../@service/ParentStudyHistory.service'
import StudyListView from '../StudyList/StudyListView'
import ParentDateRangePicker from './ParentDateRangePicker'

const ParentRecentStudySection: FC = () => {
  const service = useRepository(ParentStudyHistoryService)
  const studentId = commonRepo.parent.activeChildStudentId
  const { startDate, endDate } = service.dateRange
  const { isMobile } = useStudentAppMediaQuery()

  useEffect(() => {
    service.reset()
  }, [studentId, service])

  useEffect(() => {
    if (!studentId) return
    service.load(studentId).catch((err) => errorHandlerService.handle(err))
  }, [studentId, service, startDate, endDate])

  const allItems = service.recentStudies
  const {
    visibleItems,
    hasMobileMore,
    shouldShowMobileButton,
    handleMobileToggle,
    resetMobilePage,
  } = useMobilePagedStudyList({
    items: allItems,
    isMobile,
  })

  const handleTabChange = useCallback(
    (type: RecentStudyType) => {
      if (!studentId) return
      resetMobilePage()
      service.setFilterType(type)
      service.load(studentId).catch((err) => errorHandlerService.handle(err))
    },
    [studentId, resetMobilePage, service],
  )

  return (
    <StudyListView
      items={visibleItems}
      activeType={service.filterType}
      onChangeType={handleTabChange}
      hasMobileMore={shouldShowMobileButton ? hasMobileMore : undefined}
      onMobileLoadMore={handleMobileToggle}
      headerRightSlot={
        <ParentDateRangePicker
          startDate={startDate}
          endDate={endDate}
          onChange={(range) => service.setDateRange(range)}
        />
      }
      isReady={service.isReady}
    />
  )
}

export default observer(ParentRecentStudySection)
